// Login.jsx
import React, { useState, useEffect } from 'react';
import './Login.css';
import {
    signIn,
    signUp,
    forgotPassword,
    passwordConfirmation,
    getCurrentUser,
    confirmSignUp,
    resolvePostLoginPath,
    resolveLoginRedirectPath,
    signOut,
} from '../common/auth';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getLandingUrl } from '../common/landingLead';

const Login = () => {
    const [mode, setMode] = useState('signin');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [code, setCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [message, setMessage] = useState({ type: '', text: '' });
    const [loading, setLoading] = useState(false);
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPasswordValidation, setShowPasswordValidation] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const [hasSession, setHasSession] = useState(false);

    // Validaciones de contraseña
    const [passwordValidation, setPasswordValidation] = useState({
        hasNumber: false,
        hasSpecialChar: false,
        hasUppercase: false,
        hasLowercase: false,
        minLength: false
    });

    useEffect(() => {
        if (location.state?.message) {
            setMessage({ type: 'info', text: location.state.message });
        }
    }, [location.state?.message]);

    useEffect(() => {
        const user = getCurrentUser();
        if (!user) return;

        user.getSession(async (err, session) => {
            if (err || !session?.isValid()) return;
            setHasSession(true);
            const redirect = await resolveLoginRedirectPath();
            if (redirect) {
                navigate(redirect, { replace: true });
            }
        });
    }, [navigate]);

    // Función para validar la contraseña en tiempo real
    const validatePassword = (pwd) => {
        const validation = {
            hasNumber: /\d/.test(pwd),
            hasSpecialChar: /[\W_]/.test(pwd),
            hasUppercase: /[A-Z]/.test(pwd),
            hasLowercase: /[a-z]/.test(pwd),
            minLength: pwd.length >= 8
        };
        setPasswordValidation(validation);
        return validation;
    };

    // Verificar si todas las validaciones pasan
    const isPasswordValid = () => {
        return Object.values(passwordValidation).every(Boolean);
    };

    const handlePasswordChange = (e) => {
        const pwd = e.target.value;
        setPassword(pwd);
        
        if (mode === 'signup') {
            validatePassword(pwd);
            setShowPasswordValidation(pwd.length > 0);
        }
    };

    const handleNewPasswordChange = (e) => {
        const pwd = e.target.value;
        setNewPassword(pwd);
        
        if (mode === 'confirm') {
            validatePassword(pwd);
            setShowPasswordValidation(pwd.length > 0);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage({ type: '', text: '' });

        try {
            if (mode === 'signin') {
                const result = await signIn(email, password);
                if (result.challengeName === 'SMS_MFA') {
                    setMode('otp');
                    setMessage({
                        type: 'info',
                        text: 'MFA required. Enter the code sent to your phone.',
                    });
                } else if (result.session?.isValid?.()) {
                    const next = await resolvePostLoginPath();
                    if (next === '/login') {
                        setHasSession(true);
                        setMessage({
                            type: 'info',
                            text: location.state?.message ||
                                'No pudimos verificar tu workspace. Si ya tenés uno, intentá de nuevo en unos segundos o contactá soporte.',
                        });
                    } else {
                        navigate(next, { replace: true });
                    }
                } else {
                    setMessage({ type: 'error', text: 'No se pudo obtener la sesión. Intentá de nuevo.' });
                }
            } else if (mode === 'signup') {
                if (password !== confirmPassword) {
                    setMessage({ type: 'error', text: 'Las contraseñas no coinciden.' });
                    setLoading(false);
                    return;
                }

                if (!isPasswordValid()) {
                    setMessage({
                        type: 'error',
                        text: 'La contraseña no cumple con todos los requisitos de seguridad.',
                    });
                    setLoading(false);
                    return;
                }

                await signUp(email, password);
                setMessage({ type: 'success', text: 'Revisa tu correo para confirmar tu cuenta.' });
                setMode('verifySignup');
            } else if (mode === 'recovery') {
                await forgotPassword(email);
                setMode('confirm');
                setMessage({ type: 'info', text: 'Ingresa el código enviado a tu correo.' });
            } else if (mode === 'confirm') {
                if (!isPasswordValid()) {
                    setMessage({
                        type: 'error',
                        text: 'La nueva contraseña no cumple con todos los requisitos de seguridad.',
                    });
                    setLoading(false);
                    return;
                }

                await passwordConfirmation(email, code, newPassword);
                setMessage({ type: 'success', text: 'Contraseña restablecida exitosamente.' });
                setMode('signin');
                setShowPasswordValidation(false);
            } else if (mode === 'verifySignup') {
                await confirmSignUp(email, code);
                setMessage({ type: 'success', text: 'Cuenta confirmada. Por favor inicia sesión.' });
                setMode('signin');
            } else if (mode === 'otp') {
                setMessage({ type: 'info', text: 'MFA verification in progress...' });
            }
        } catch (err) {
            const text =
                typeof err?.message === 'string' && err.message.startsWith('{')
                    ? 'No se pudo completar el inicio de sesión. Intentá de nuevo.'
                    : err.message || 'Algo salió mal';
            setMessage({ type: 'error', text });
        } finally {
            setLoading(false);
        }
    };

    const getWelcomeText = () => {
        switch (mode) {
            case 'signin':
                return { title: '¡Te damos la bienvenida!', subtitle: 'Completa tu información para ingresar' };
            case 'signup':
                return { title: 'Crear cuenta', subtitle: 'Únete a nuestra plataforma' };
            case 'recovery':
                return { title: 'Recuperar contraseña', subtitle: 'Te ayudaremos a recuperar el acceso' };
            case 'confirm':
                return { title: 'Confirmar nueva contraseña', subtitle: 'Ingresa el código de verificación' };
            case 'verifySignup':
                return { title: 'Verificar cuenta', subtitle: 'Ingresa el código de confirmación' };
            case 'otp':
                return { title: 'Autenticación MFA', subtitle: 'Ingresa el código de seguridad' };
            default:
                return { title: '¡Te damos la bienvenida!', subtitle: 'Completa tu información para ingresar' };
        }
    };

    const { title, subtitle } = getWelcomeText();

    return (
        <div className="login-container">
            {/* Panel izquierdo con iconos */}
            <div className="login-left-panel">
                <div className="login-icons-grid">
                    {[...Array(20)].map((_, i) => (
                        <div key={i} className="login-icon-tile"></div>
                    ))}
                </div>
                
     
            </div>

            {/* Panel derecho con formulario */}
            <div className="login-right-panel">
                <div className="login-form-wrapper">
                    <a href={getLandingUrl()} className="login-back-landing">
                        ← Volver a {new URL(getLandingUrl()).hostname}
                    </a>
                    <div className="login-welcome">
                        <h1>{title}</h1>
                        <p>{subtitle}</p>
                    </div>

                    <form className="login-form" onSubmit={handleSubmit}>
                        {/* Campo de email */}
                        {mode !== 'otp' && (
                            <div className="login-input-group">
                                <input
                                    type="email"
                                    className="login-input"
                                    placeholder="EMAIL"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={loading || mode === 'confirm' || mode === 'verifySignup'}
                                    required
                                />
                            </div>
                        )}

                        {/* Campos para signin y signup */}
                        {(mode === 'signin' || mode === 'signup') && (
                            <>
                                <div className="login-input-group">
                                    <input
                                        type="password"
                                        className="login-input"
                                        placeholder="CONTRASEÑA"
                                        value={password}
                                        onChange={handlePasswordChange}
                                        disabled={loading}
                                        required
                                    />
                                    
                                    {/* Validación de contraseña para signup */}
                                    {mode === 'signup' && showPasswordValidation && (
                                        <div className="password-validation-container">
                                            <h4>Requisitos de contraseña</h4>
                                            <div className="password-requirements">
                                                <div className={`requirement ${passwordValidation.hasNumber ? 'valid' : 'invalid'}`}>
                                                    <span className="requirement-icon">
                                                        {passwordValidation.hasNumber ? '✓' : '✗'}
                                                    </span>
                                                    <span className="requirement-text">Contiene al menos 1 número</span>
                                                </div>
                                                
                                                <div className={`requirement ${passwordValidation.hasSpecialChar ? 'valid' : 'invalid'}`}>
                                                    <span className="requirement-icon">
                                                        {passwordValidation.hasSpecialChar ? '✓' : '✗'}
                                                    </span>
                                                    <span className="requirement-text">Contiene al menos 1 carácter especial</span>
                                                </div>
                                                
                                                <div className={`requirement ${passwordValidation.hasUppercase ? 'valid' : 'invalid'}`}>
                                                    <span className="requirement-icon">
                                                        {passwordValidation.hasUppercase ? '✓' : '✗'}
                                                    </span>
                                                    <span className="requirement-text">Contiene al menos una letra mayúscula</span>
                                                </div>
                                                
                                                <div className={`requirement ${passwordValidation.hasLowercase ? 'valid' : 'invalid'}`}>
                                                    <span className="requirement-icon">
                                                        {passwordValidation.hasLowercase ? '✓' : '✗'}
                                                    </span>
                                                    <span className="requirement-text">Contiene al menos una letra minúscula</span>
                                                </div>
                                                
                                                <div className={`requirement ${passwordValidation.minLength ? 'valid' : 'invalid'}`}>
                                                    <span className="requirement-icon">
                                                        {passwordValidation.minLength ? '✓' : '✗'}
                                                    </span>
                                                    <span className="requirement-text">Mínimo 8 caracteres</span>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                
                                {mode === 'signup' && (
                                    <div className="login-input-group">
                                        <input
                                            type="password"
                                            className="login-input"
                                            placeholder="CONFIRMAR CONTRASEÑA"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            disabled={loading}
                                            required
                                        />
                                    </div>
                                )}
                            </>
                        )}

                        {/* Campo de código de verificación */}
                        {(mode === 'verifySignup' || mode === 'otp' || mode === 'confirm') && (
                            <div className="login-input-group">
                                <input
                                    type="text"
                                    className="login-input"
                                    placeholder={mode === 'otp' ? 'CÓDIGO MFA' : 'CÓDIGO DE VERIFICACIÓN'}
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                    disabled={loading}
                                    required
                                />
                            </div>
                        )}

                        {/* Nueva contraseña para reseteo */}
                        {mode === 'confirm' && (
                            <div className="login-input-group">
                                <input
                                    type="password"
                                    className="login-input"
                                    placeholder="NUEVA CONTRASEÑA"
                                    value={newPassword}
                                    onChange={handleNewPasswordChange}
                                    disabled={loading}
                                    required
                                />
                                
                                {/* Validación de contraseña para confirm */}
                                {showPasswordValidation && (
                                    <div className="password-validation-container">
                                        <h4>Requisitos de contraseña</h4>
                                        <div className="password-requirements">
                                            <div className={`requirement ${passwordValidation.hasNumber ? 'valid' : 'invalid'}`}>
                                                <span className="requirement-icon">
                                                    {passwordValidation.hasNumber ? '✓' : '✗'}
                                                </span>
                                                <span className="requirement-text">Contiene al menos 1 número</span>
                                            </div>
                                            
                                            <div className={`requirement ${passwordValidation.hasSpecialChar ? 'valid' : 'invalid'}`}>
                                                <span className="requirement-icon">
                                                    {passwordValidation.hasSpecialChar ? '✓' : '✗'}
                                                </span>
                                                <span className="requirement-text">Contiene al menos 1 carácter especial</span>
                                            </div>
                                            
                                            <div className={`requirement ${passwordValidation.hasUppercase ? 'valid' : 'invalid'}`}>
                                                <span className="requirement-icon">
                                                    {passwordValidation.hasUppercase ? '✓' : '✗'}
                                                </span>
                                                <span className="requirement-text">Contiene al menos una letra mayúscula</span>
                                            </div>
                                            
                                            <div className={`requirement ${passwordValidation.hasLowercase ? 'valid' : 'invalid'}`}>
                                                <span className="requirement-icon">
                                                    {passwordValidation.hasLowercase ? '✓' : '✗'}
                                                </span>
                                                <span className="requirement-text">Contiene al menos una letra minúscula</span>
                                            </div>
                                            
                                            <div className={`requirement ${passwordValidation.minLength ? 'valid' : 'invalid'}`}>
                                                <span className="requirement-icon">
                                                    {passwordValidation.minLength ? '✓' : '✗'}
                                                </span>
                                                <span className="requirement-text">Mínimo 8 caracteres</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Link de contraseña olvidada */}
                        {mode === 'signin' && (
                            <a 
                                href="#" 
                                className="login-forgot-link"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setMode('recovery');
                                }}
                            >
                                ¿Olvidaste tu contraseña?
                            </a>
                        )}

                        {/* Botón de submit */}
                        <button
                            type="submit"
                            className="login-submit-btn"
                            disabled={loading}
                        >
                            {loading
                                ? 'Procesando...'
                                : mode === 'signin'
                                    ? 'Iniciar Sesión'
                                    : mode === 'signup'
                                        ? 'Registrarse'
                                        : mode === 'recovery'
                                            ? 'Enviar Código'
                                            : mode === 'confirm'
                                                ? 'Restablecer Contraseña'
                                                : mode === 'verifySignup'
                                                    ? 'Verificar Cuenta'
                                                    : 'Verificar'}
                        </button>

                        {/* Mensajes de error/éxito */}
                        {message.text && (
                            <div className={`login-message ${message.type}`}>
                                {message.text}
                            </div>
                        )}

                        {hasSession && mode === 'signin' && (
                            <button
                                type="button"
                                className="ob-btn-secondary"
                                style={{ marginTop: 12, width: '100%' }}
                                onClick={() => {
                                    signOut();
                                    setHasSession(false);
                                    setMessage({ type: 'info', text: 'Sesión cerrada.' });
                                }}
                            >
                                Cerrar sesión
                            </button>
                        )}
                    </form>

                    {/* Sección de registro/login */}
                    <div className="login-signup">
                        {mode === 'signin' ? (
                            <p>
                                ¿No tienes cuenta?
                                <Link to="/onboarding/cuenta" className="login-signup-link">
                                    Solicitá acceso
                                </Link>
                            </p>
                        ) : (
                            <p>
                                ¿Ya tienes cuenta?
                                <a 
                                    href="#" 
                                    className="login-signup-link"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setMode('signin');
                                        setMessage({ type: '', text: '' });
                                        setShowPasswordValidation(false);
                                    }}
                                >
                                    Inicia sesión
                                </a>
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;